import { useEffect, useState } from "react";
import { type DailyCloseoutReportRecord, type ProviderVenue, providerApi } from "../../lib/api";
import { useAppPreferences } from "../../lib/preferences";
import { subscribeShiftChanged } from "../provider-shift/useCashShift";

function todayLocalDate() {
  return new Date().toISOString().slice(0, 10);
}

function formatMoney(amount: number, currency: string, locale: string) {
  return new Intl.NumberFormat(locale, {
    currency,
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    style: "currency",
  }).format(amount);
}

function formatDateTime(value: string, locale: string) {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

const paymentMethods = ["cash", "card", "transfer", "other"];

export function ProviderReportsCard({ selectedVenue }: { selectedVenue: ProviderVenue | null }) {
  const { resolvedLanguage, t } = useAppPreferences();
  const locale = resolvedLanguage === "ar" ? "ar-EG" : "en-US";
  const [dateLocal, setDateLocal] = useState(todayLocalDate);
  const [report, setReport] = useState<DailyCloseoutReportRecord | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadReport = async () => {
    if (!selectedVenue) {
      setReport(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const nextReport = await providerApi.getDailyCloseoutReport({
        dateLocal,
        venueId: selectedVenue.id,
      });
      setReport(nextReport);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : t("Could not load daily closeout."));
    } finally {
      setIsLoading(false);
    }
  };

  // The report's till section should follow shifts started or ended elsewhere on the page.
  useEffect(() => subscribeShiftChanged(() => void loadReport()));

  useEffect(() => {
    void loadReport();
  }, [dateLocal, selectedVenue?.id]);

  if (!selectedVenue) {
    return <div className="ops-empty">{t("Select a venue to view reports.")}</div>;
  }

  const currency = report?.currency ?? "EGP";
  const drawer = report?.cash_drawer ?? null;
  const variance = report?.variance_amount ?? null;

  const kpiItems = report
    ? [
        [t("Issued"), formatMoney(report.issued_total_amount, currency, locale)],
        [t("Paid"), formatMoney(report.paid_total_amount, currency, locale)],
        [t("Unpaid"), formatMoney(report.unpaid_total_amount, currency, locale)],
        [t("Receipts"), String(report.receipt_count)],
        [t("Payments"), String(report.transaction_count)],
      ]
    : [];

  return (
    <div className="ops-surface grid content-start gap-3">
      <div className="ops-page-header border-b border-[var(--color-border-sub)] pb-3">
        <div className="min-w-0">
          <p className="ops-kicker">{t("Daily closeout")}</p>
          <p className="truncate text-xs text-slate-400">{selectedVenue.name}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            className="ops-input w-[9rem] py-1.5 text-xs"
            onChange={(event) => setDateLocal(event.target.value)}
            type="date"
            value={dateLocal}
          />
          <button
            className="ops-button"
            data-size="sm"
            data-variant="secondary"
            disabled={isLoading}
            onClick={() => void loadReport()}
            type="button"
          >
            {isLoading ? t("Refreshing...") : t("Refresh")}
          </button>
        </div>
      </div>

      {error ? <div className="ops-banner" data-tone="danger">{error}</div> : null}
      {isLoading && !report ? <div className="ops-empty">{t("Loading closeout...")}</div> : null}

      {report ? (
        <div className="grid gap-3">
          <div className="provider-money-tiles">
            {kpiItems.map(([label, value]) => (
              <div className="ops-stat-card ops-stat-card-dense" key={label}>
                <p className="ops-stat-label">{label}</p>
                <p className="ops-stat-value">{value}</p>
              </div>
            ))}
          </div>

          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_260px]">
            <div className="grid gap-2">
              <div className="flex items-center justify-between gap-2">
                <p className="ops-kicker">{t("Payment methods")}</p>
                <span className="ops-chip">{report.date_local}</span>
              </div>
              <div className="provider-money-tiles" data-size="sm">
                {paymentMethods.map((method) => (
                  <div
                    className="ops-stat-card ops-stat-card-dense"
                    key={method}
                  >
                    <p className="ops-stat-label">{t(method)}</p>
                    <p className="ops-stat-value">
                      {formatMoney(report.payment_totals[method] ?? 0, currency, locale)}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="ops-subtle-surface grid content-start gap-2 p-3">
              <div className="flex items-start justify-between gap-2">
                <p className="ops-kicker">{t("Cash till (shift)")}</p>
                {drawer ? (
                  <span
                    className="ops-chip shrink-0"
                    data-tone={drawer.status === "open" ? "success" : "info"}
                  >
                    {drawer.status === "open" ? t("Shift open") : t("Shift ended")}
                  </span>
                ) : (
                  <span className="ops-chip shrink-0">{t("No shift")}</span>
                )}
              </div>

              <div className="grid gap-1 text-xs">
                <div className="flex justify-between gap-2">
                  <span className="text-slate-400">{t("Should be in the till")}</span>
                  <span>{formatMoney(report.expected_cash_amount, currency, locale)}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-slate-400">{t("You counted")}</span>
                  <span>
                    {report.counted_cash_amount === null
                      ? t("Not counted")
                      : formatMoney(report.counted_cash_amount, currency, locale)}
                  </span>
                </div>
                <div className="flex justify-between gap-2 border-t border-white/10 pt-1 font-semibold">
                  <span className="text-slate-300">{t("Short / over")}</span>
                  <span
                    className={
                      variance === null || variance === 0
                        ? "text-white"
                        : variance > 0
                          ? "text-emerald-200"
                          : "text-red-200"
                    }
                  >
                    {variance === null ? t("Shift not ended") : formatMoney(variance, currency, locale)}
                  </span>
                </div>
              </div>

              {drawer ? (
                <div className="grid gap-1 text-[11px] leading-tight text-slate-400">
                  <div className="flex justify-between gap-2">
                    <span>{t("Started")}</span>
                    <span>{formatDateTime(drawer.opened_at, locale)}</span>
                  </div>
                  {drawer.closed_at ? (
                    <div className="flex justify-between gap-2">
                      <span>{t("Ended")}</span>
                      <span>{formatDateTime(drawer.closed_at, locale)}</span>
                    </div>
                  ) : null}
                </div>
              ) : null}
              <p className="text-[11px] text-slate-500">
                {t("Generated")}: {formatDateTime(report.generated_at, locale)}
              </p>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
